/**
 * MarketplaceBackgroundVideo - Spinning-globe background video for marketplace browse pages
 *
 * GW-9355: Restores the spinning-globe background on the live marketplace browse
 * pages (applications, datasets, services, missions).
 *
 * GW-9376/GW-10438: Mounted ONCE in the shared globe-hosting layout route (the
 * unified AppLayout shell's `showGlobeBackground` host, passed as LayoutShell's
 * `background` slot), NOT per-page — so a single persistent <video> node is
 * shared across the landing/browse/auth page set within each auth state and the
 * globe never restarts on navigation. NOT mounted globally in LayoutShell.
 *
 * Renders a non-interactive looping video that fills its positioned parent
 * (`absolute inset-0`), followed by a dual-opacity overlay (85% light / 60% dark)
 * that keeps foreground text readable in both themes. Both layers use
 * `pointer-events-none` so they never intercept clicks.
 *
 * GW-10550: the content-lift contract (`relative z-10` over this backdrop) now
 * lives in LayoutShell, which renders this as a flex sibling of the scroller
 * sized to the visible content region and lifts the scroller above it — so the
 * globe stays fixed to the region and no longer restretches as content grows.
 */

import { cn } from '@gw/ui';

interface MarketplaceBackgroundVideoProps {
  className?: string;
}

export function MarketplaceBackgroundVideo({ className }: MarketplaceBackgroundVideoProps) {
  return (
    <>
      <video
        data-testid="marketplace-background-video"
        autoPlay
        loop
        muted
        playsInline
        className={cn('pointer-events-none absolute inset-0 h-full w-full object-cover', className)}
      >
        <source src="/globe.mp4" type="video/mp4" />
      </video>
      <div className="bg-background/85 dark:bg-background/60 pointer-events-none absolute inset-0" />
    </>
  );
}
